import { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { ROLE_PERMISSIONS, hasEffectivePermission } from '../utils/permissions';

const STX_ROLES = ['stx_admin', 'stx_ops'];

/**
 * UIDs reporting to managerUid, walking the hierarchy `depth` levels deep.
 * `members` only needs `{ id, managerId }` on each entry.
 */
function reachableReports(managerUid, members, depth) {
  const reachable = new Set();
  let frontier = members.filter(m => m.managerId === managerUid).map(m => m.id);
  let lvl = 0;

  while (frontier.length > 0 && lvl < depth) {
    const cur = frontier;
    cur.forEach(uid => reachable.add(uid));
    lvl += 1;
    frontier = lvl < depth
      ? members.filter(m => cur.includes(m.managerId) && !reachable.has(m.id)).map(m => m.id)
      : [];
  }
  return reachable;
}

/**
 * Resolves the effective approval scope for the given user.
 *
 * Returns:
 *   null        — can approve trips for ALL users in the client
 *   Set<uid>    — can only approve trips where trip.travellerId is in this set
 *   'none'      — has no approval permission at all
 *
 * When approveScope === 'reports', subscribes to the client's users collection
 * to build the reporting hierarchy set in real time.
 */
export function useApprovalScope(userProfile, clientId) {
  const [members, setMembers] = useState([]);

  const role     = userProfile?.role;
  const isSTX    = STX_ROLES.includes(role);
  const overrides = userProfile?.permissionOverrides || {};
  const roleHasApprove = !isSTX && !!(ROLE_PERMISSIONS[role]?.includes('trip:approve'));
  const hasApprovePermission = 'trip:approve' in overrides
    ? overrides['trip:approve'] === true
    : roleHasApprove;

  const approveScope       = userProfile?.approveScope;
  const approveFor         = userProfile?.approveFor || [];
  const effectiveScope     = approveScope ?? (approveFor.length > 0 ? 'select' : 'all');
  const approveReportsDepth = userProfile?.approveReportsDepth || 1;

  // Stable primitive key to avoid re-running memo on array identity changes
  const approveForKey = approveFor.join(',');

  useEffect(() => {
    if (isSTX || !hasApprovePermission) return;
    if (effectiveScope !== 'reports') return;
    if (!clientId || !userProfile?.uid) return;

    const q = query(collection(db, 'users'), where('clientId', '==', clientId));
    const unsub = onSnapshot(q, snap => {
      setMembers(snap.docs.map(d => ({ id: d.id, managerId: d.data().managerId })));
    });
    return unsub;
  }, [isSTX, hasApprovePermission, effectiveScope, clientId, userProfile?.uid]);

  return useMemo(() => {
    if (isSTX) return null;
    if (!hasApprovePermission) return 'none';
    if (effectiveScope === 'all') return null;

    if (effectiveScope === 'select') {
      // Empty select list falls back to "all" for backward compatibility
      if (!approveForKey) return null;
      return new Set(approveForKey.split(','));
    }

    if (effectiveScope === 'reports') {
      const myUid = userProfile?.uid;
      if (!myUid || members.length === 0) return new Set();
      return reachableReports(myUid, members, approveReportsDepth);
    }

    return null;
  }, [isSTX, hasApprovePermission, effectiveScope, approveForKey, approveReportsDepth, members, userProfile?.uid]);
}

/**
 * Returns true if the given approvalScope (from useApprovalScope) permits
 * approving the given trip.
 *
 * Pass `uid` so an approver nominated on the trip itself is recognised. Nomination widens an
 * existing approver's reach to one traveller they'd otherwise have no scope over — it never
 * grants approval rights on its own, so a nominee whose trip:approve was since revoked
 * (scope 'none') still can't act.
 */
export function matchesApprovalScope(scope, trip, uid) {
  if (scope === 'none') return false;
  if (scope === null)   return true;
  if (uid && trip?.nominatedApproverId === uid) return true;
  if (!trip?.travellerId) return false;
  return scope.has(trip.travellerId);
}

/**
 * Effective trip:approve for a user record, as a client-side approver.
 *
 * STX staff are deliberately excluded. They can already approve anything, so listing them as
 * nominees would offer a client an approver they don't own and wouldn't hold accountable.
 */
function memberCanApprove(member) {
  if (!member || member.active === false) return false;
  if (STX_ROLES.includes(member.role))    return false;
  return hasEffectivePermission(member, 'trip:approve');
}

/** Active client users who can approve trips at all — the pool a trip can nominate from. */
export function eligibleApprovers(members = []) {
  return members.filter(memberCanApprove);
}

/**
 * An approver's effective scope, applying the same backward-compat defaults as the hook above:
 * an absent approveScope means 'select' when approveFor has entries, otherwise 'all'.
 */
function scopeOf(member) {
  const approveFor = member.approveFor || [];
  return member.approveScope ?? (approveFor.length > 0 ? 'select' : 'all');
}

/**
 * Approvers specifically accountable for this traveller: their own direct manager, someone who
 * named them in an approveFor list, or someone whose reports scope reaches them.
 *
 * **Catch-all ('all') approvers are deliberately NOT counted.** Approve-for-everyone is typically
 * granted as a fallback rather than as an active approval role, so those people are unlikely to be
 * watching for requests and would miss one that landed on them by default. They can still approve
 * the trip and are still emailed — they just don't satisfy the requirement that someone be
 * accountable for this particular traveller.
 *
 * The direct manager counts whatever their scope, including catch-all: a blanket grant is a
 * fallback for everyone else, but for their own report it *is* the reporting line. They must still
 * hold trip:approve though (eligibleApprovers enforces it) — a manager who can't approve is not an
 * approval route, and treating them as one would leave the same gap wearing a different hat.
 */
export function specificApproversFor(members = [], travellerId) {
  // No travellerId at all means the traveller isn't a portal user — a guest speaker, say —
  // so they have no manager and no scope can name or reach them.
  if (!travellerId) return [];
  const traveller = members.find(m => m.id === travellerId);

  return eligibleApprovers(members).filter(m => {
    if (traveller?.managerId && m.id === traveller.managerId) return true;
    const scope = scopeOf(m);
    if (scope === 'select')  return (m.approveFor || []).includes(travellerId);
    if (scope === 'reports') return reachableReports(m.id, members, m.approveReportsDepth || 1).has(travellerId);
    return false;
  });
}

/**
 * Does this trip have to nominate its own approver before it can be submitted?
 *
 * True for any traveller nobody is specifically accountable for — the board member, the guest
 * speaker, the contractor nobody manages — regardless of whether the client has catch-all
 * approvers who could step in.
 */
export function needsApproverNomination(members = [], travellerId) {
  // Members arrive via onSnapshot; an empty list means "not loaded yet", not "no approvers",
  // or the warning flashes up on every form open.
  if (!members.length) return false;
  return specificApproversFor(members, travellerId).length === 0;
}
